import type { ProceduralTimelineEvent } from './procedural-timeline-event.entity.js';

/** How far before the event a reminder (VALARM) fires. `None` emits no alarm. */
export enum CalendarReminder {
  None = 'none',
  OneHour = 'one_hour',
  OneDay = 'one_day',
  OneWeek = 'one_week',
}

// RFC 5545 relative TRIGGER durations, measured back from the event start.
const REMINDER_TRIGGERS: Record<Exclude<CalendarReminder, CalendarReminder.None>, string> = {
  [CalendarReminder.OneHour]: '-PT1H',
  [CalendarReminder.OneDay]: '-P1D',
  [CalendarReminder.OneWeek]: '-P1W',
};

export interface BuildEventIcsOptions {
  wexlerUrl: string;
  matterName?: string;
  reminder?: CalendarReminder;
  prodIdSuffix?: string;
}

const DEFAULT_DURATION_MS = 60 * 60 * 1000;

/**
 * A downloadable, self-contained VEVENT (METHOD:PUBLISH). Stable {@link makeUid|UID} plus a
 * monotonic SEQUENCE from `updatedAt` means re-importing after an edit updates the entry in place
 * rather than duplicating it.
 */
export function buildEventIcs(event: ProceduralTimelineEvent, opts: BuildEventIcsOptions): string {
  if (event.startDateTime === null) {
    throw new Error('Cannot build calendar file for an event with no start date');
  }

  const start = new Date(event.startDateTime);
  // Procedural dates with no time-of-day are stored at midnight (the "no time
  // set" sentinel). Those become all-day events.
  const isAllDay = !hasTimeComponent(start);
  const stamp = new Date();

  const tzId = event.timeZone;
  const uid = makeUid(event.id);
  const sequence = Math.floor(new Date(event.updatedAt).getTime() / 1000);

  const reminder = opts.reminder ?? CalendarReminder.None;
  const summary =
    opts.matterName !== undefined && opts.matterName.trim() !== ''
      ? `${event.name} - ${opts.matterName.trim()}`
      : event.name;

  let dtStartLine: string;
  let dtEndLine: string;
  if (isAllDay) {
    const lastDay = event.endDateTime !== null ? new Date(event.endDateTime) : start;
    dtStartLine = `DTSTART;VALUE=DATE:${formatIcsDate(start)}`;
    dtEndLine = `DTEND;VALUE=DATE:${formatIcsDate(addUtcDay(lastDay))}`;
  } else {
    const end =
      event.endDateTime !== null
        ? new Date(event.endDateTime)
        : new Date(start.getTime() + DEFAULT_DURATION_MS);
    dtStartLine = formatIcsDateTime('DTSTART', start, tzId);
    dtEndLine = formatIcsDateTime('DTEND', end, tzId);
  }

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:-//Wexler//Procedural Timeline${opts.prodIdSuffix !== undefined ? ` ${opts.prodIdSuffix}` : ''}//EN`,
    'METHOD:PUBLISH',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatIcsDateTimeUtc(stamp)}`,
    `SEQUENCE:${sequence}`,
    dtStartLine,
    dtEndLine,
    `SUMMARY:${escapeText(summary)}`,
    `DESCRIPTION:${escapeText(buildFallbackDescription(event, opts.wexlerUrl))}`,
    `X-ALT-DESC;FMTTYPE=text/html:${escapeText(buildHtmlDescription(event, opts.wexlerUrl))}`,
    `URL:${escapeText(opts.wexlerUrl)}`,
  ];

  // RFC 5545 §3.6.5: every referenced TZID needs a VTIMEZONE
  if (!isAllDay && tzId !== null && tzId.trim() !== '') {
    lines.splice(lines.indexOf('BEGIN:VEVENT'), 0, ...buildVTimezone(tzId, start));
  }

  lines.push('STATUS:CONFIRMED');
  if (isAllDay) {
    lines.push(
      'TRANSP:TRANSPARENT',
      'X-MICROSOFT-CDO-ALLDAYEVENT:TRUE',
      'X-MICROSOFT-CDO-BUSYSTATUS:FREE',
    );
  } else {
    lines.push('TRANSP:OPAQUE');
  }

  if (reminder !== CalendarReminder.None) {
    // A single DISPLAY alarm relative to the start. On all-day events the
    // trigger is measured from midnight of the start date.
    lines.push(
      'BEGIN:VALARM',
      'ACTION:DISPLAY',
      `DESCRIPTION:${escapeText(summary)}`,
      `TRIGGER:${REMINDER_TRIGGERS[reminder]}`,
      'END:VALARM',
    );
  }

  lines.push('END:VEVENT', 'END:VCALENDAR');

  return `${lines.map(foldLine).join('\r\n')}\r\n`;
}

function buildFallbackDescription(event: ProceduralTimelineEvent, wexlerUrl: string): string {
  const parts: string[] = [];
  if (event.description !== null && event.description.trim() !== '') {
    parts.push(event.description.trim());
  }
  parts.push(`View the event in Wexler: ${wexlerUrl}`);
  return parts.join('\n\n');
}

// HTML body for X-ALT-DESC.
function buildHtmlDescription(event: ProceduralTimelineEvent, wexlerUrl: string): string {
  const parts: string[] = [];
  if (event.description !== null && event.description.trim() !== '') {
    parts.push(`<p>${escapeHtml(event.description.trim())}</p>`);
  }
  parts.push(`<p>View the event in <a href="${escapeHtml(wexlerUrl)}">Wexler</a>.</p>`);
  return `<html><body>${parts.join('')}</body></html>`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Stable per-event UID; never recompute differently anywhere else. */
export function makeUid(eventId: string): string {
  return `procedural-${eventId}@wexler.ai`;
}

export function buildInviteFilename(event: ProceduralTimelineEvent): string {
  const slug = event.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${slug !== '' ? slug : 'event'}.ics`;
}

// Minimal VTIMEZONE pinning the single UTC offset in effect at the event start.
// Not a full DST ruleset, but RFC 5545 requires a VTIMEZONE for any referenced
// TZID, and a single observance unambiguously fixes a point-in-time event.
function buildVTimezone(tzId: string, at: Date): string[] {
  const offset = formatUtcOffset(zoneOffsetMinutes(tzId, at));
  return [
    'BEGIN:VTIMEZONE',
    `TZID:${tzId}`,
    'BEGIN:STANDARD',
    'DTSTART:19700101T000000',
    `TZOFFSETFROM:${offset}`,
    `TZOFFSETTO:${offset}`,
    'END:STANDARD',
    'END:VTIMEZONE',
  ];
}

// UTC offset (minutes) of `tzId` at `at`, read from Intl's "GMT±HH:MM" name.
function zoneOffsetMinutes(tzId: string, at: Date): number {
  let name: string | undefined;
  try {
    name = new Intl.DateTimeFormat('en-US', { timeZone: tzId, timeZoneName: 'longOffset' })
      .formatToParts(at)
      .find((part) => part.type === 'timeZoneName')?.value;
  } catch {
    // A malformed timeZone (bad DB value) makes Intl throw RangeError. Fall back
    // to UTC.
    return 0;
  }
  const match = name !== undefined ? /GMT([+-])(\d{2}):(\d{2})/.exec(name) : null;
  if (match === null) return 0; // UTC/GMT render as "GMT" with no offset
  return (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]));
}

function formatUtcOffset(minutes: number): string {
  const abs = Math.abs(minutes);
  return `${minutes < 0 ? '-' : '+'}${String(Math.floor(abs / 60)).padStart(2, '0')}${String(abs % 60).padStart(2, '0')}`;
}

// With a timezone, emit naive YYYYMMDDTHHMMSS + TZID; otherwise UTC `…Z`.
function formatIcsDateTime(prop: 'DTSTART' | 'DTEND', date: Date, tzId: string | null): string {
  if (tzId !== null && tzId.trim() !== '') {
    return `${prop};TZID=${tzId}:${formatIcsDateTimeLocal(date)}`;
  }
  return `${prop}:${formatIcsDateTimeUtc(date)}`;
}

// Naive timestamps are read back with UTC getters locked to the stored
// wall-clock values, so midnight (no time set) reads as 00:00:00 in UTC.
function hasTimeComponent(date: Date): boolean {
  return date.getUTCHours() !== 0 || date.getUTCMinutes() !== 0 || date.getUTCSeconds() !== 0;
}

// Date-only value for all-day events: YYYYMMDD, no time and no timezone.
function formatIcsDate(date: Date): string {
  return `${date.getUTCFullYear()}${String(date.getUTCMonth() + 1).padStart(2, '0')}${String(date.getUTCDate()).padStart(2, '0')}`;
}

// Next calendar day in UTC; Date.UTC handles month/year rollover.
function addUtcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1));
}

// UTC components treated as wall-clock; the caller decides whether to append Z.
function formatIcsDateTimeLocal(date: Date): string {
  return (
    `${date.getUTCFullYear()}${String(date.getUTCMonth() + 1).padStart(2, '0')}${String(date.getUTCDate()).padStart(2, '0')}` +
    `T${String(date.getUTCHours()).padStart(2, '0')}${String(date.getUTCMinutes()).padStart(2, '0')}${String(date.getUTCSeconds()).padStart(2, '0')}`
  );
}

function formatIcsDateTimeUtc(date: Date): string {
  return `${formatIcsDateTimeLocal(date)}Z`;
}

function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

// RFC 5545: 75-octet lines, continuations prefixed with a single space.
function foldLine(line: string): string {
  if (utf8Bytes(line) <= 75) return line;
  const segments: string[] = [];
  let current = '';
  let bytes = 0;
  // First physical line allows 75 octets; continuations reserve one for the
  // leading space. `for..of` iterates whole code points, so a multi-byte
  // character is never split across the fold.
  let budget = 75;
  for (const char of line) {
    const size = utf8Bytes(char);
    if (bytes + size > budget && current !== '') {
      segments.push(current);
      current = '';
      bytes = 0;
      budget = 74;
    }
    current += char;
    bytes += size;
  }
  segments.push(current);
  return segments.map((segment, index) => (index === 0 ? segment : ` ${segment}`)).join('\r\n');
}

const utf8Encoder = new TextEncoder();
function utf8Bytes(value: string): number {
  return utf8Encoder.encode(value).length;
}
